import type { FC } from "react";
import { memo, useEffect, useRef, useState } from "react";

import type { AnimationConfigWithPath, AnimationItem, LottiePlayer } from "lottie-web";

import PulseLoading from "@/design-system/pulse-loading";
import { Loading } from "@Components/Loading";
import type { PropsWithStyleX } from "@stylex/tokens.stylex";
import * as stylexjs from "@stylexjs/stylex";

const styles = stylexjs.create({
    hideAnimation: {
        transition: "width 0.5s 0.5s, height 0.5s 0.5s, opacity 0.5s",
        opacity: 0,
        height: 0,
        width: 0,
    },
    showAnimation: {
        transition: "width 0.5s, height 0.5s, opacity 0.5s 0.5s",
        opacity: 1,
        height: "100%",
        width: "100%",
    },
});

export type ILottieAnimation = PropsWithStyleX & {
    autoplay?: boolean;
    loop?: boolean | number;
    animationPath?: string;
    playingType?: string;
    onDataReady?: () => void;
    onCompleteLoop?: () => void;
    stopAnimationAt?: number;
    pulseLoadingClass?: stylexjs.StyleXStyles;
};

let lottie: LottiePlayer = null;

export const LottieAnimation: FC<ILottieAnimation> = memo(
    ({
        loop,
        autoplay,
        animationPath,
        playingType,
        pulseLoadingClass,
        onDataReady,
        onCompleteLoop,
        stopAnimationAt,
        styleX,
    }) => {
        const [isLoading, setIsLoading] = useState(true);
        const [scriptLoading, setScriptLoading] = useState(lottie === null);
        const [lottieInstance, setLottieInstance] = useState<AnimationItem>();
        const lottieContainer = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (lottie === null) {
                import(/* webpackChunkName: "lottie-web" */ "lottie-web").then((r) => {
                    lottie = r.default;
                    setScriptLoading(false);
                });
            }
        }, []);

        useEffect(() => {
            if (!lottieContainer.current || scriptLoading) {
                return;
            }

            const defaultOptions: AnimationConfigWithPath = {
                container: lottieContainer.current,
                renderer: "svg",
                loop,
                autoplay,
                path: animationPath,
                rendererSettings: {
                    preserveAspectRatio: "xMidYMid meet",
                    progressiveLoad: true,
                },
            };

            const newLottieInstance = lottie.loadAnimation(defaultOptions);
            newLottieInstance.addEventListener("data_ready", () => {
                if (onDataReady) {
                    onDataReady();
                }
                setIsLoading(false);
            });

            newLottieInstance.addEventListener("loopComplete", () => {
                if (onCompleteLoop) {
                    onCompleteLoop();
                }
            });

            newLottieInstance.addEventListener("complete", () => {
                if (stopAnimationAt) {
                    const totalTime = newLottieInstance.getDuration(false);
                    const stopAt = totalTime - stopAnimationAt;

                    if (stopAt > 0 && stopAt < totalTime) {
                        newLottieInstance.goToAndStop(stopAt * 1000, false);
                    }
                }
            });

            setLottieInstance(newLottieInstance);

            return () => newLottieInstance.destroy();
        }, [scriptLoading]);

        useEffect(() => {
            if (lottieInstance) {
                if (playingType === "play") {
                    lottieInstance.play();
                } else if (playingType === "pause") {
                    lottieInstance.pause();
                } else if (playingType === "stop") {
                    lottieInstance.stop();
                }
            }
        }, [playingType]);

        const showAnimation = isLoading ? styles.hideAnimation : styles.showAnimation;

        const loadingComponent = (): JSX.Element | null => {
            if (isLoading) {
                if (pulseLoadingClass) {
                    return <PulseLoading stylexItem={pulseLoadingClass} />;
                }
                return <Loading isModal={true} />;
            }
            return null;
        };

        return (
            <div {...stylexjs.props(styleX)}>
                <div {...stylexjs.props(showAnimation)} ref={lottieContainer} />
                {loadingComponent()}
            </div>
        );
    },
    (prevProps, nextProps) => nextProps.playingType === prevProps.playingType,
);
